import { AnimatePresence, motion } from 'framer-motion';
import { isEqual } from 'lodash';
import { useState } from 'react';

import { Disclaimer } from '@common/ui/Disclaimer';
import { useEventListener, EventType } from '@common/utils/events';

interface Notification {
  id: number;
  type: EventType;
  title: string;
  description?: string;
}

export interface NotificationsProviderProps {
  children?: React.ReactNode;
}

export const NotificationsProvider = ({
  children,
}: NotificationsProviderProps) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const addNotification = (notify: Notification) => {
    setNotifications((state) => {
      const existingNotify = state.find((i) => isEqual(i, notify));
      return !existingNotify ? [...state, notify] : state;
    });
  };

  const removeNotification = (id: number) => {
    setNotifications((state) => state.filter((item) => item.id !== id));
  };

  useEventListener('error', (event) => {
    addNotification({
      id: Math.random(),
      type: 'error',
      ...event,
    });
  });

  useEventListener('info', (event) => {
    addNotification({
      id: Math.random(),
      type: 'info',
      ...event,
    });
  });

  useEventListener('success', (event) => {
    addNotification({
      id: Math.random(),
      type: 'success',
      ...event,
    });
  });

  useEventListener('warning', (event) => {
    addNotification({
      id: Math.random(),
      type: 'warning',
      ...event,
    });
  });

  return (
    <>
      <div
        style={{
          maxWidth: 'var(--max-width)',
          margin: 'auto',
          padding: 16,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          // Above the full-screen sheets (1001) so their feedback stays readable.
          zIndex: 1002,
          display: notifications.length > 0 ? 'flex' : 'none',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <AnimatePresence>
          {notifications.reverse().map((notify) => (
            <motion.div
              key={notify.id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onAnimationComplete={() =>
                setTimeout(() => removeNotification(notify.id), 5000)
              }
            >
              <div
                style={{
                  background: 'var(--layer-floor-1)',
                  borderRadius: 16,
                }}
              >
                <Disclaimer
                  type={notify.type}
                  title={notify.title}
                  body={notify.description}
                  visible
                  onClose={() => removeNotification(notify.id)}
                />
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
      {children}
    </>
  );
};
